// src/features/rfq/NewRfqModal.tsx
import React, { useState } from 'react';
import { Modal } from '../../components/Modal';
import { useToast } from '../../context/ToastContext';
import { generationErrorText } from './generationError';
import { createManualRfqWithFile, createManualRfqWithFileTest, reuploadRfqWithFile } from '../../lib/rfqApi';
import DateInput from "../../components/DateInput";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  mode?: 'create' | 'reupload' | 'test';
  targetRfqId?: string;
}

export const NewRfqModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onCreated,
  mode = 'create',
  targetRfqId,
}) => {
  const isReupload = mode === 'reupload';
  const isTest = mode === 'test';
  const [rfqNumber, setRfqNumber] = useState('');
  const [date, setDate] = useState('');
  // D109.6: false while the date box holds a refused date (`date` is then the OLD one).
  const [dateValid, setDateValid] = useState(true);
  const [clientName, setClientName] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (f: File | null) => {
    if (!f) {
      setFile(null);
      return;
    }

    const allowed = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    if (!allowed.includes(f.type)) {
      setError('Only PDF, images, or Excel/CSV files are allowed.');
      return;
    }

    if (f.size > 10 * 1024 * 1024) {
      setError('File is too large. Maximum allowed is 10 MB.');
      return;
    }

    setError(null);
    setFile(f);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!file) {
      setError('Please attach a valid RFQ file.');
      return;
    }

    // Marked required above, so it is refused here — a marker the form does not enforce is
    // just a different way of failing silently. Reupload keeps the RFQ's existing client, so
    // it does not ask. (ClickUp 86d4advfz)
    if (!isReupload && !clientName.trim()) {
      setError('Client Name is required.');
      return;
    }

    if (!isReupload && !dateValid) {
      setError('Fix the date first: it is not a real day.');
      return;
    }

    if (isReupload && !targetRfqId) {
      setError('No RFQ selected for reupload.');
      return;
    }

    const form = new FormData();
    form.append('file', file);
    if (!isReupload) {
      if (rfqNumber) form.append('rfqId', rfqNumber);
      form.append('customerName', clientName.trim());
      if (subject) form.append('subject', subject);
      if (description) form.append('description', description);
      if (date) form.append('date', date);
    }

    // D103.15: reading the file can take a while (a PDF is read by a model), so the form closes
    // at once and the user keeps working. The outcome arrives as a toast; the list refreshes
    // when it succeeds. The failure toast names the server's machine-readable code, never prose.
    const fileName = file.name;
    onClose();
    toast.info(`Reading ${fileName}...`);
    setFile(null);
    try {
      const rfq = isReupload
        ? await reuploadRfqWithFile(targetRfqId as string, form)
        : isTest
        ? await createManualRfqWithFileTest(form)
        : await createManualRfqWithFile(form);
      toast.success(`RFQ ${rfq.rfqId} ${isReupload ? 'updated' : 'created'}`);
      onCreated();
    } catch (err) {
      const code = (err as { code?: string } | null)?.code;
      toast.error(`Could not read ${fileName}: ${code ? generationErrorText(code) : 'the server could not be reached'}`);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isReupload ? 'Reupload RFQ File' : isTest ? 'New RFQ Test' : 'Add New RFQ'}
      maxWidthClass="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-3 text-xs">
        {!isReupload && (
          <>
            {/* RFQ Number + Date */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block mb-1 text-xs font-medium text-slate-600">
                  RFQ Number
            </label>
            <input
              className="w-full h-9 border border-slate-200 rounded-sm px-2 text-xs bg-white"
              value={rfqNumber}
              onChange={(e) => setRfqNumber(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="w-32">
            <label className="block mb-1 text-xs font-medium text-slate-600">
              Date
            </label>
            <DateInput
              className="w-full h-9 border border-slate-200 rounded-sm px-2 text-xs bg-white"
              value={date}
              onChange={(v) => setDate(v)}
              validity={setDateValid}
            />
            </div>
          </div>

            {/* Client Name — the only field on this form that is required. It is marked as
                such because the form said nothing and the failure came later: without it the
                RFQ is minted as RFQ-<date>-UNKNOWN-<n>, links to no client, and every screen
                downstream that resolves a client by name starts from a blank.
                (ClickUp 86d4advfz) */}
            <div>
              <label className="block mb-1 text-xs font-medium text-slate-600">
                Client Name <span className="text-rose-500" aria-hidden="true">*</span>
                <span className="ml-1 font-normal text-slate-400">Required</span>
              </label>
          <input
            className="w-full h-9 border border-slate-200 rounded-sm px-2 text-xs bg-white"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="e.g. Maersk Line"
            required
            aria-required="true"
            />
          </div>

            {/* Subject */}
            <div>
              <label className="block mb-1 text-xs font-medium text-slate-600">
                Subject
              </label>
          <input
            className="w-full h-9 border border-slate-200 rounded-sm px-2 text-xs bg-white"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. RFQ for Provisions"
            />
          </div>

            {/* Description */}
            <div>
              <label className="block mb-1 text-xs font-medium text-slate-600">
                Description
              </label>
          <textarea
            className="w-full border border-slate-200 rounded-sm px-2 py-1.5 text-xs bg-white min-h-[80px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Additional notes..."
            />
          </div>
          </>
        )}

        {/* File Upload */}
        <div>
          <label className="block mb-1 text-xs font-medium text-slate-600">
            Attach File
          </label>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center px-3 h-8 bg-slate-50 border border-slate-300 rounded-sm text-xs cursor-pointer hover:bg-slate-100">
              Choose File
              <input
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.csv"
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
            </label>
            <span className="text-xs text-slate-500 truncate">
              {file ? file.name : 'No file chosen'}
            </span>
          </div>
        </div>

        {error && <div className="text-xs text-red-500">{error}</div>}

        {/* Footer */}
        <div className="pt-4">
          <button
            type="submit"
            className="w-full py-3 rounded-md bg-primary-500 border-b-4 border-primary-600 text-white text-sm font-bold hover:bg-primary-600 active:border-b-0 active:mt-1 transition-all"
          >
            {isReupload ? 'Upload RFQ' : isTest ? 'Create RFQ test' : 'Create RFQ'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
